import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Globe, ShoppingCart, TrendUp, ArrowRight, Star, Lightning, 
  CheckCircle, Sparkle, Palette, Code, Rocket, MagnifyingGlass, 
  Gauge, Users, Shield
} from '@phosphor-icons/react';

gsap.registerPlugin(ScrollTrigger);

const ModernServices = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Cards animation on scroll
      gsap.fromTo(cardsRef.current.children,
        {
          y: 100,
          opacity: 0,
          scale: 0.8
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "back.out(1.7)",
          stagger: 0.2,
          scrollTrigger: {
            trigger: cardsRef.current,
            start: "top 80%",
            end: "bottom 20%",
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const services = [
    {
      icon: <Globe weight="duotone" className="w-16 h-16" />,
      title: t('modernServices.websites.title'),
      subtitle: t('modernServices.websites.subtitle'),
      description: t('modernServices.websites.description'),
      features: [
        t('modernServices.websites.features.responsive'),
        t('modernServices.websites.features.seo'),
        t('modernServices.websites.features.fast'),
        t('modernServices.websites.features.cms')
      ],
      href: '/webdesign',
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      iconBg: 'from-blue-100 to-blue-200',
      subIcons: [<Palette weight="duotone" />, <Code weight="duotone" />, <Lightning weight="duotone" />]
    },
    {
      icon: <ShoppingCart weight="duotone" className="w-16 h-16" />,
      title: t('modernServices.webshops.title'),
      subtitle: t('modernServices.webshops.subtitle'),
      description: t('modernServices.webshops.description'),
      features: [
        t('modernServices.webshops.features.payments'),
        t('modernServices.webshops.features.inventory'),
        t('modernServices.webshops.features.analytics'),
        t('modernServices.webshops.features.marketing')
      ],
      href: '/webshops',
      color: 'blue',
      gradient: 'from-blue-400 to-blue-500',
      bgGradient: 'from-blue-50 to-blue-100',
      iconBg: 'from-blue-100 to-blue-200',
      subIcons: [<Rocket weight="duotone" />, <Star weight="duotone" />, <Users weight="duotone" />]
    },
    {
      icon: <TrendUp weight="duotone" className="w-16 h-16" />,
      title: t('modernServices.optimization.title'),
      subtitle: t('modernServices.optimization.subtitle'),
      description: t('modernServices.optimization.description'),
      features: [
        t('modernServices.optimization.features.seo'),
        t('modernServices.optimization.features.speed'),
        t('modernServices.optimization.features.analytics'),
        t('modernServices.optimization.features.monitoring')
      ],
      href: '/seo',
      color: 'blue',
      gradient: 'from-blue-600 to-blue-700',
      bgGradient: 'from-blue-50 to-blue-100',
      iconBg: 'from-blue-100 to-blue-200',
      subIcons: [<MagnifyingGlass weight="duotone" />, <Gauge weight="duotone" />, <Shield weight="duotone" />],
      subLinks: [
        { label: t('modernServices.optimization.subLinks.seo'), href: '/seo' },
        { label: t('modernServices.optimization.subLinks.speed'), href: '/website-snelheid' }
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="py-32 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-blue-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-100/20 to-blue-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-full border border-blue-200/50 mb-6">
            <Sparkle weight="duotone" className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">{t('modernServices.badge')}</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            {t('modernServices.title.part1')}{' '}
            <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              {t('modernServices.title.part2')}
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 leading-relaxed">
            {t('modernServices.subtitle')}
          </p>
        </motion.div>

        <div ref={cardsRef} className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              whileHover={{ y: -10 }}
              className="group relative h-full"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} rounded-3xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300`} />
              
              <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-white/50 group-hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                {/* Icon and Sub-icons */}
                <div className="relative mb-6">
                  <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${service.iconBg} rounded-2xl text-${service.color}-600 mb-4`}>
                    {service.icon}
                  </div>
                  
                  {/* Floating sub-icons */}
                  <div className="absolute -top-2 -right-2 flex gap-2">
                    {service.subIcons.map((icon, idx) => (
                      <div 
                        key={idx}
                        className={`w-8 h-8 bg-gradient-to-br ${service.iconBg} rounded-lg flex items-center justify-center text-${service.color}-500 opacity-0 group-hover:opacity-100 transition-all duration-500`}
                        style={{ animationDelay: `${idx * 100}ms` }}
                      >
                        {React.cloneElement(icon, { className: 'w-4 h-4' })}
                      </div>
                    ))}
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {service.title}
                </h3>
                
                <p className={`text-${service.color}-600 font-medium mb-4`}>
                  {service.subtitle}
                </p>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8 flex-grow">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle weight="duotone" className={`w-5 h-5 text-${service.color}-500`} />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3 mt-auto">
                  <Link
                    to={service.href}
                    className={`w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r ${service.gradient} text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 group-hover:shadow-lg transform group-hover:-translate-y-1`}
                  >
                    {t('modernServices.learnMore')}
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  
                  {service.subLinks && (
                    <div className="grid grid-cols-2 gap-2">
                      {service.subLinks.map((link, idx) => (
                        <Link
                          key={idx}
                          to={link.href}
                          className={`text-center px-3 py-2 bg-${service.color}-50 text-${service.color}-700 rounded-lg text-sm font-medium hover:bg-${service.color}-100 transition-colors duration-200`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ModernServices;
